import { useState } from 'react'
import { Table, Input, Select, Space, Button, Typography, Tag } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useDraftList } from '@/modules/draft/queries'
import StatusTag from '@/components/feedback/StatusTag'
import ErrorResult from '@/components/feedback/ErrorResult'
import { formatMessageTime } from '@/utils/datetime'
import type { DraftItem } from '@/types/draft'

const { Text } = Typography

export default function DraftListPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [sortValue, setSortValue] = useState('created_at.desc')

  const { data, isLoading, isError, error, refetch } = useDraftList({
    page,
    page_size: pageSize,
    keyword: keyword || undefined,
    status: statusFilter,
    sort_by: sortValue.split('.')[0],
    sort_order: sortValue.split('.')[1],
  })

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space wrap>
        <Input
          placeholder="搜索客户/标题/摘要"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 260 }}
          value={keyword}
          onChange={(e) => { setKeyword(e.target.value); setPage(1) }}
        />
        <Select
          placeholder="草稿状态"
          allowClear
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={[
            { label: '待审核', value: 'drafted' },
            { label: '待补信息', value: 'need_more_info' },
            { label: '待补绑定', value: 'binding_required' },
            { label: '已确认', value: 'confirmed' },
            { label: '已建单', value: 'created_task' },
            { label: '不建单', value: 'rejected' },
          ]}
        />
        <Select
          aria-label="排序"
          style={{ width: 180 }}
          value={sortValue}
          onChange={(v) => { setSortValue(v); setPage(1) }}
          options={[
            { label: '最新产生在前', value: 'created_at.desc' },
            { label: '最早产生在前', value: 'created_at.asc' },
            { label: '最近更新在前', value: 'updated_at.desc' },
            { label: '置信度高在前', value: 'confidence.desc' },
            { label: '置信度低在前', value: 'confidence.asc' },
          ]}
        />
      </Space>

      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) },
        }}
        columns={[
          { title: '产生时间', dataIndex: 'created_at', width: 120, render: (v: string) => formatMessageTime(v) },
          { title: '编号', dataIndex: 'draft_no', width: 140 },
          { title: '客户', dataIndex: 'customer_name', width: 100, render: (v: string) => v || <Text type="secondary">未绑定</Text> },
          { title: '项目', dataIndex: 'project_name', width: 130, ellipsis: true, render: (v: string) => v || '-' },
          { title: '标题', dataIndex: 'task_title', ellipsis: true },
          { title: '优先级', dataIndex: 'priority', width: 60 },
          { title: '状态', dataIndex: 'status', width: 100, render: (v: string) => <StatusTag type="draft" status={v} /> },
          {
            title: '来源标签', width: 130,
            render: (_: unknown, record: DraftItem) => {
              if (record.trigger_source?.source_type === 'wechat_group_trigger') {
                return <Tag color="processing" style={{ fontSize: 11 }}>微信群触发</Tag>
              }
              if (record.status === 'binding_required') {
                return <Tag color="red" style={{ fontSize: 11 }}>待补绑定</Tag>
              }
              if (record.status === 'confirmed') {
                return <Tag color="orange" style={{ fontSize: 11 }}>待选研发</Tag>
              }
              return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>
            },
          },
          {
            title: '置信度', dataIndex: 'confidence', width: 80,
            render: (v: number) => {
              const color = v >= 0.8 ? 'green' : v >= 0.6 ? 'orange' : 'red'
              return <Tag color={color}>{(v * 100).toFixed(0)}%</Tag>
            },
          },
          {
            title: '风险', dataIndex: 'risk_flags', width: 120,
            render: (v: string[]) => v?.length > 0 ? v.map((f) => <Tag key={f} color="red" style={{ fontSize: 11 }}>{f}</Tag>) : '-',
          },
          {
            title: '操作', width: 100, render: (_: unknown, r: { id: string }) => (
              <Button type="link" size="small" onClick={() => navigate(`/drafts/${r.id}`)}>审核</Button>
            ),
          },
        ]}
      />
    </div>
  )
}
